import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, KeyRound } from "lucide-react";
import { Logo } from "./Logo";

const links = [
  { id: "services", href: "#services", label: "Services" },
  { id: "avantages", href: "#avantages", label: "Avantages" },
  { id: "zone", href: "#zone", label: "Zone d'intervention" },
  { id: "contact", href: "#contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "border-b border-[#D4AF37]/20 bg-black/80 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-medium text-zinc-300 transition-colors hover:text-[#E6CA65]"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-5 lg:flex">
          <a
            href="tel:0602259560"
            data-testid="nav-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-white transition-colors hover:text-[#E6CA65]"
          >
            <Phone className="h-4 w-4 text-[#D4AF37]" />
            06 02 25 95 60
          </a>
          <a
            href="#rendez-vous"
            data-testid="nav-booking-cta"
            className="rounded-full bg-[#D4AF37] px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#F3E5AB]"
          >
            Prendre rendez-vous
          </a>
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="text-zinc-500 transition-colors hover:text-[#E6CA65]"
            aria-label="Espace pro"
          >
            <KeyRound className="h-4 w-4" />
          </Link>
        </div>
        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-[#D4AF37]/20 bg-black/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-5 px-6 py-6">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-zinc-200"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#rendez-vous"
                data-testid="nav-mobile-booking-cta"
                onClick={() => setOpen(false)}
                className="rounded-full bg-[#D4AF37] px-6 py-3 text-center text-xs font-bold uppercase tracking-widest text-black"
              >
                Prendre rendez-vous
              </a>
              <a href="tel:0602259560" className="flex items-center gap-2 text-sm font-semibold text-white">
                <Phone className="h-4 w-4 text-[#D4AF37]" /> 06 02 25 95 60
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
